import {produceHttpEvent} from '@shopify/monorail';
import {UnderscoreCasedPayload} from '@shopify/monorail/lib/events/events';
// Code referenced from: https://github.com/Shopify/payment-sheet/blob/156d5b2049c166a742d7b34e2539f0cfc47cd51f/src/Analytics/tracking.ts

import {Nilable, isThemeEditor} from 'utilities/common';
import {
  DeliveryEstimates,
  QueryMetadata,
} from 'utilities/delivery-promise/types';
import {hasFasterOptions} from 'utilities/delivery-promise/helpers/promise.helpers';
import {readShopifyFeatures} from 'utilities/shopify-features';
import {getConfiguration} from 'utilities/configuration';

import {PromiseMonorailEvent, PromiseMonorailEventV2} from './types';
import {DELIVERY_PROMISE_FRONTEND_EVENT} from './consts';

export const APP_VERSION = 'react-shop-promise-pdp/1.5';

/**
 * Verifies if ShopifyAnalytics is already loaded into the window. if true
 * calls ShopifyAnalytics to track a given event. otherwise just enqueue the
 * event to track later when the module is loaded.
 */
export async function produceMonorailEvent(
  monorailEvent: PromiseMonorailEvent | PromiseMonorailEventV2,
  onError?: (error: any) => void,
) {
  try {
    if (isThemeEditor()) return;
    if (isDuplicate(monorailEvent)) return;

    const {releaseStage} = getConfiguration();

    if (releaseStage === 'development') {
      // eslint-disable-next-line no-console
      console.log(
        `[ShopPromise][Monorail] [EventType=${monorailEvent.payload.eventType}] [Schema=${monorailEvent.schemaId}]`,
        monorailEvent.payload,
      );
      return;
    }

    await produceHttpEvent({
      event: toUnderscoreCase(monorailEvent),
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[ShopPromise] failed to send Monorail event', error);
    onError?.(error);
  }
}

const TREKKIE_POLL_INTERVAL_MS = 100;
const TREKKIE_TIMEOUT_MS = 7000;

export async function getTrekkieDefaultAttributes() {
  if (!window?.ShopifyAnalytics?.lib?.trekkie) {
    await new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (window?.ShopifyAnalytics?.lib?.trekkie) {
          clearInterval(interval);
          resolve();
        }
      }, TREKKIE_POLL_INTERVAL_MS);
      const timeout = setTimeout(() => {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve();
      }, TREKKIE_TIMEOUT_MS);
    });
  }

  const trekkieDefaultAttributes =
    window.ShopifyAnalytics?.lib?.trekkie?.defaultAttributes;

  return {
    uniqToken: trekkieDefaultAttributes?.uniqToken ?? '',
    visitToken: trekkieDefaultAttributes?.visitToken ?? '',
    microSessionId: trekkieDefaultAttributes?.microSessionId ?? '',
    microSessionCount: trekkieDefaultAttributes?.microSessionCount ?? 0,
    shopId:
      trekkieDefaultAttributes?.shopId ?? readShopifyFeatures().shopId ?? 0,
    themeId: trekkieDefaultAttributes?.themeId ?? 0,
    themeCityHash: trekkieDefaultAttributes?.themeCityHash ?? '',
    contentLanguage: trekkieDefaultAttributes?.contentLanguage ?? '',
    referer: trekkieDefaultAttributes?.referer,
  };
}

export async function defaultMonorailEventPayload() {
  const trekkieDefaultAttributes = await getTrekkieDefaultAttributes();
  return {
    visitToken: trekkieDefaultAttributes.visitToken,
    uniqToken: trekkieDefaultAttributes.uniqToken,
    microSessionId: trekkieDefaultAttributes.microSessionId,
    shopId: trekkieDefaultAttributes.shopId,
    url: window.location.href,
    appVersion: APP_VERSION,
    pageType: 'product',
    eventTimestamp: new Date(Date.now()).toISOString(),
  };
}

export function storePageEventPromisePayload(
  deliveryEstimate: Nilable<DeliveryEstimates & QueryMetadata>,
) {
  return {
    deliveryPromiseDateRangeMin: toDate(
      deliveryEstimate?.selectedShippingOption?.minDeliveryTime,
    )?.toISOString(),
    deliveryPromiseDateRangeMax: toDate(
      deliveryEstimate?.selectedShippingOption?.maxDeliveryTime,
    )?.toISOString(),
    destinationTimezone: deliveryEstimate?.deliveryAddress?.timezone,
    shippingCost:
      Number(deliveryEstimate?.selectedShippingOption?.cost?.amount) ||
      undefined,
    minCalendarDaysToDelivery:
      deliveryEstimate?.selectedShippingOption?.minCalendarDaysToDelivery ||
      undefined,
    maxCalendarDaysToDelivery:
      deliveryEstimate?.selectedShippingOption?.maxCalendarDaysToDelivery ||
      undefined,
    deliveryDateMissingReason:
      deliveryEstimate?.deliveryDateMissingReason || undefined,
    isBrandShown: Boolean(
      deliveryEstimate?.selectedShippingOption?.brandedPromise,
    ),
    expiryTime:
      deliveryEstimate?.selectedShippingOption?.expiresAt || undefined,
    freeDeliveryThreshold:
      Number(deliveryEstimate?.freeDeliveryThreshold?.amount) || undefined,
    countryCode: deliveryEstimate?.deliveryAddress?.countryCodeV2,
    provinceCode: deliveryEstimate?.deliveryAddress?.provinceCode,
    deliveryPromiseText:
      deliveryEstimate?.selectedShippingOption?.deliveryPromisePresentmentTitle,
    isFasterOptionsLabelShown: hasFasterOptions(deliveryEstimate),
    requestId: deliveryEstimate?.requestId || undefined,
  };
}

export function deliveryPromiseResponseEventType(
  deliveryEstimate: DeliveryEstimates,
  postalCodeOverride: Nilable<string>,
) {
  let eventType = 'delivery_promise_for_buyer_location_shown';
  if (!postalCodeOverride) {
    if (deliveryEstimate.deliveryAddress.zip) {
      eventType = 'delivery_promise_for_geo_ip_zip_code_shown';
    } else if (deliveryEstimate.deliveryAddress.city) {
      eventType = 'delivery_promise_for_geo_ip_city_shown';
    } else if (deliveryEstimate.deliveryAddress.provinceCode) {
      eventType = 'delivery_promise_for_geo_ip_state_shown';
    } else {
      eventType = 'delivery_promise_for_geo_ip_country_shown';
    }
  }
  return eventType;
}

// ----------------------------------------------------------------

function toDate(date: Nilable<string>): Nilable<Date> {
  return date ? new Date(date) : null;
}

function getExludedFieldsBySchemaId(schemaId: string) {
  /**
   * These fields are specified in our schemas as camelCase
   */
  const defaultExlusions = ['uniqToken', 'visitToken', 'microSessionId'];
  switch (schemaId) {
    case DELIVERY_PROMISE_FRONTEND_EVENT:
      return [];
    default:
      return defaultExlusions;
  }
}

function toUnderscoreCase<
  TEventFamily extends PromiseMonorailEvent | PromiseMonorailEventV2,
>(object: TEventFamily): UnderscoreCasedPayload<TEventFamily> {
  const exludedFields = getExludedFieldsBySchemaId(object.schemaId);

  // no the best ever typing, but it can cope the problem
  const originalPayload = object.payload as {[key: string]: unknown};
  const payload = Object.keys(originalPayload)
    .map((key) => ({
      // reconstruct the object with the keys in underscore_case
      [exludedFields.includes(key) ? key : convertStringToUnderscoreCase(key)]:
        originalPayload[key],
    }))
    .reduce((prev, next) => ({
      ...prev,
      ...next,
    })) as UnderscoreCasedPayload<TEventFamily>['payload'];

  return {
    schemaId: object.schemaId,
    payload,
  } as UnderscoreCasedPayload<TEventFamily>;
}

function convertStringToUnderscoreCase(camelCaseString: string) {
  return camelCaseString
    .split(/(?=[A-Z])/)
    .join('_')
    .toLowerCase();
}

const eventsDeduplication = new Map<string, number>();

const createEventDeduplicationKey = (...props: (string | number | boolean)[]) =>
  props.join('$');

export function clearEventsDeduplication() {
  eventsDeduplication.clear();
}

const DEDUPLICATION_TTL_MS = 2000;

/**
 * To avoid multiple events being sent to Monorail due to unstable values in hooks and components
 *
 * @param monorailEvent
 * @returns boolean
 */
function isDuplicate(
  monorailEvent: PromiseMonorailEvent | PromiseMonorailEventV2,
): boolean {
  const hash = createEventDeduplicationKey(
    monorailEvent.schemaId,
    ...Object.entries(monorailEvent.payload)
      .filter(([key]) => key !== 'eventTimestamp')
      .map(([_, value]) => value),
  );

  if (eventsDeduplication.has(hash)) {
    if (Date.now() - eventsDeduplication.get(hash)! > DEDUPLICATION_TTL_MS) {
      eventsDeduplication.delete(hash);
      return false;
    }
    return true;
  }

  eventsDeduplication.set(hash, Date.now());

  return false;
}
